<script lang="ts">
  /**
   * 공개 페이지 미리보기 — `/@:username` 을 iframe 으로 **실제 렌더**한다.
   * (정본: `.planning/profile-visibility.md` 결정 3)
   *
   * 컴포넌트를 재사용해 편집기 데이터로 다시 그리지 않는다. 방문자가 받는 그 문서를
   * 그대로 띄우므로 필터·표시값이 어긋날 여지가 없다.
   *
   * 대가 두 가지와 그 처리:
   * 1. 링크·그룹·프로필의 편집 중인 값은 반영되지 않는다 → 저장이 끝나면 iframe 을
   *    다시 띄운다(아래 $effect). **디자인만 예외다** — 저장 전 테마는 같은 출처
   *    postMessage 로 iframe 에 넘겨 그 자리에서 다시 그린다. 공개 판정(무엇이 보이는지)은
   *    여전히 서버 문서가 맡고, 이 채널은 색·폰트·배경만 덮는다.
   *    (프리셋을 골라도 오른쪽이 그대로라 "안 먹는다"고 느끼던 문제의 수리, 2026-09-15)
   * 2. 소유자 세션으로 열리므로 그냥 두면 소유자 전용 표시가 섞인다
   *    → `?preview=1` 로 서버가 로그아웃 시점과 같게 렌더한다
   *      (`PublicProfilesController#show` 의 `preview_as_visitor`).
   */
  import { groups, links, profile, theme, themeDirty } from '../stores/editor';

  // `Public/Profile.svelte` 와 맞춘 메시지 이름. 바꾸면 양쪽을 같이 바꾼다.
  const THEME_PREVIEW_MESSAGE = 'axhub:theme-preview';
  const THEME_PREVIEW_READY_MESSAGE = 'axhub:theme-preview-ready';
  // 슬라이더·색상 피커는 한 번에 수십 번 바뀐다. 마지막 값만 보낸다.
  const THEME_POST_DEBOUNCE_MS = 80;

  let iframeEl = $state<HTMLIFrameElement | null>(null);
  let themePostTimer: ReturnType<typeof setTimeout> | undefined;

  function postThemeToPreview() {
    const target = iframeEl?.contentWindow;
    if (!target || typeof window === 'undefined') return;
    // 🔴 JSON 으로 한 번 거른다. 테마에는 Inertia props(Svelte `$state` Proxy)에서
    //    퍼 온 값이 섞여 있어 그대로 넘기면 structured clone 이 DataCloneError 로
    //    조용히 끊긴다 — 로컬 실측(2026-09-15). 같은 출처로만 보낸다.
    let payload: unknown;
    try {
      payload = JSON.parse(JSON.stringify($theme));
    } catch (error) {
      console.warn('[PhonePreview] 미리보기 테마를 직렬화하지 못했습니다.', error);
      return;
    }
    target.postMessage({ type: THEME_PREVIEW_MESSAGE, theme: payload }, window.location.origin);
  }

  function scheduleThemePost() {
    clearTimeout(themePostTimer);
    themePostTimer = setTimeout(postThemeToPreview, THEME_POST_DEBOUNCE_MS);
  }

  // 저장 전 변경이 있는 동안만 테마를 밀어 넣는다. 저장 직후에는 iframe 이 새로 뜨고
  // 그 문서가 이미 저장된 테마를 그리므로 덮을 것이 없다.
  $effect(() => {
    const current = $theme;
    const dirty = $themeDirty;
    void current;
    if (!dirty) return;
    scheduleThemePost();
  });

  // iframe 문서가 (다시) 뜨면 준비 신호를 보낸다. 링크를 저장해 iframe 이 새로 떴는데
  // 디자인은 아직 저장 전이면, 그 변경을 다시 얹어야 미리보기가 뒤로 가지 않는다.
  $effect(() => {
    if (typeof window === 'undefined') return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== iframeEl?.contentWindow) return;
      if (event.data?.type !== THEME_PREVIEW_READY_MESSAGE) return;
      if ($themeDirty) postThemeToPreview();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  });

  $effect(() => () => clearTimeout(themePostTimer));

  // 저장 직후 여러 스토어가 잇달아 갱신되므로 마지막 변경만 반영한다.
  const RELOAD_DEBOUNCE_MS = 700;

  let reloadToken = $state(0);
  let reloadTimer: ReturnType<typeof setTimeout> | undefined;

  const previewPath = $derived(
    $profile.public_url || ($profile.username ? `/@${$profile.username}` : '')
  );
  const previewSrc = $derived(previewPath ? `${previewPath}?preview=1&v=${reloadToken}` : '');

  const publicUrlLabel = $derived.by(() => {
    if (!previewPath) return '';
    if (typeof window === 'undefined') return previewPath;
    return `${window.location.host}${previewPath}`;
  });

  // 방문자에게 안 보이는 링크 수 — 서버가 내린 판정(is_publicly_visible)만 세고,
  // 공개/예약/만료 규칙을 여기서 다시 계산하지 않는다.
  const hiddenLinkCount = $derived.by(() => {
    const hiddenGroupIds = new Set(
      $groups.filter((group) => group.is_publicly_visible !== true).map((group) => group.id)
    );
    return $links.filter(
      (link) =>
        link.is_publicly_visible !== true ||
        (link.link_group_id ? hiddenGroupIds.has(link.link_group_id) : false)
    ).length;
  });

  function reloadPreview() {
    clearTimeout(reloadTimer);
    reloadToken += 1;
  }

  function scheduleReload() {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadToken += 1;
    }, RELOAD_DEBOUNCE_MS);
  }

  // 링크·그룹·프로필 스토어는 서버 응답으로만 교체된다(낙관적 갱신 없음).
  // 테마는 슬라이더를 미는 동안에도 바뀌므로, 저장으로 dirty 가 풀린 순간만 신호로 본다.
  let previous: {
    links: unknown;
    groups: unknown;
    profile: unknown;
    themeDirty: boolean;
    seen: boolean;
  } = { links: null, groups: null, profile: null, themeDirty: false, seen: false };

  $effect(() => {
    const nextLinks = $links;
    const nextGroups = $groups;
    const nextProfile = $profile;
    const dirty = $themeDirty;

    const dataChanged =
      nextLinks !== previous.links ||
      nextGroups !== previous.groups ||
      nextProfile !== previous.profile;
    const themeSaved = previous.themeDirty && !dirty;
    const firstRun = !previous.seen;

    previous = {
      links: nextLinks,
      groups: nextGroups,
      profile: nextProfile,
      themeDirty: dirty,
      seen: true,
    };

    // 최초 렌더는 src 가 이미 띄운다.
    if (firstRun) return;
    if (dataChanged || themeSaved) scheduleReload();
  });

  $effect(() => () => clearTimeout(reloadTimer));
</script>

<div class="sticky top-6 max-h-[calc(100vh-2rem)] overflow-y-auto xl:top-8">
  {#if publicUrlLabel}
    <div class="mx-auto mb-3 flex w-[320px] items-center justify-center gap-1.5">
      <span
        class="min-w-0 truncate rounded-full border border-gray-200 bg-white px-3 py-1 font-mono text-[11px] text-gray-600"
        title={publicUrlLabel}
      >
        {publicUrlLabel}
      </span>
      <button
        type="button"
        class="shrink-0 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-600 transition hover:bg-gray-50"
        onclick={reloadPreview}
      >
        새로고침
      </button>
    </div>
  {/if}

  <div class="mx-auto w-[320px] rounded-[2.25rem] border border-gray-300 bg-gray-950 p-3 shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
    <div class="rounded-[1.8rem] bg-gray-950 p-2">
      <div class="phone-viewport flex h-[640px] overflow-hidden rounded-[1.45rem] border border-black/5 bg-white">
        {#if previewSrc}
          <iframe
            bind:this={iframeEl}
            title="공개 페이지 미리보기"
            src={previewSrc}
            class="h-full w-full border-0"
          ></iframe>
        {:else}
          <p class="m-auto break-keep px-6 text-center text-xs leading-5 text-gray-500">
            사용자명을 정하면
            <br />
            공개 페이지가 여기에 열립니다.
          </p>
        {/if}
      </div>
    </div>
  </div>

  <p class="mx-auto mt-3 w-[320px] break-keep text-center text-[11px] leading-4 text-gray-500">
    공개 페이지를
    <br />
    방문자 시점으로 띄웁니다.
    <br />
    디자인 변경은 바로 보이지만
    <br />
    저장해야 방문자에게 반영됩니다.
    {#if $profile.profile_public === false}
      <br />
      지금은 비공개 상태라 방문자에게는 열리지 않습니다.
    {:else if hiddenLinkCount > 0}
      <br />
      숨김·예약·만료 링크 {hiddenLinkCount}개는 방문자에게 안 보입니다.
    {/if}
  </p>
</div>
